import {useCallback, useEffect, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {useSetting} from "contexts/setting";
import {useCurrentSet} from "hooks/useCurrentSet";
import {DEFAULT_ROUTE_PATTERNS, useRouteParams} from "hooks/useRouteParams";
import {homeScope} from "data/offering";
import {
  backgroundOfNumber,
  namesUrlBackground,
  readUrlBackground,
  writeUrlBackground,
} from "libs/bgNumbering";

// ---------------------------------------------------------------------------
// The numbered tiles of the page (libs/bgNumbering.ts), and the background
// that the URL names.
//
// App knows the tiles of the page: it publishes their numbers for the page's
// gallery (usePublishBackgroundNumbers), and the URL readers and writers read
// them (useBackgroundNumbers). This is the pattern of useBgCatalogIndex. React
// Query stays the only cache.
// ---------------------------------------------------------------------------

interface Scope {
  sceneId?: string;
  channel?: string;
  type?: string;
  product?: string;
  set?: string;
}

/** One page gallery: a saved link, or a (channel, type, product, set). */
export function backgroundNumbersScope({sceneId, channel, type, product, set}: Scope): string {
  return sceneId ? `scene:${sceneId}` : [channel, type, product, set ?? ""].join(":");
}

export const getBackgroundNumbersQueryKey = (scope: string) => ["bg-numbers", scope] as const;

/** The gallery of the page, from the setting context, and whether it is the URL's. */
function usePageScope(): {scope: string; isSynced: boolean} {
  const {sceneId} = useParams();
  const {dataChannel, dataType, product}: any = useSetting();
  const set = useCurrentSet();
  const routeParams: Record<string, string | undefined> = useRouteParams(DEFAULT_ROUTE_PATTERNS);
  const scope = backgroundNumbersScope({
    sceneId,
    channel: dataChannel,
    type: dataType,
    product: product?.value,
    set,
  });
  // The context trails the URL by one render on a product or channel switch
  // (memory: context lags URL). Until it catches up, the numbers of the page
  // before are no numbers of this URL.
  const route = homeScope(routeParams.channel, routeParams.contentType, routeParams.product);
  const isSynced =
    Boolean(sceneId) ||
    !routeParams.channel ||
    (dataChannel === route.channel &&
      product?.value === routeParams.product &&
      dataType === route.type);
  return {scope, isSynced};
}

/**
 * The variations of the numbered tiles of the page (tile N is entry N - 1),
 * or null while App has not published them, and on a page without numbers.
 */
export function useBackgroundNumbers(): number[] | null {
  const {scope, isSynced} = usePageScope();
  const {data} = useQuery({
    queryKey: getBackgroundNumbersQueryKey(scope),
    queryFn: (): number[] => {
      throw new Error("bg-numbers is published by App, never fetched");
    },
    enabled: false,
  });
  return isSynced ? (data ?? null) : null;
}

/** App: publish the numbered tiles of the page. Null publishes nothing. */
export function usePublishBackgroundNumbers(scope: Scope, numbered: number[] | null): void {
  const queryClient = useQueryClient();
  const key = backgroundNumbersScope(scope);
  useEffect(() => {
    if (!numbered) return;
    queryClient.setQueryData(getBackgroundNumbersQueryKey(key), numbered);
  }, [numbered, key, queryClient]);
}

/**
 * The background that a number names, for each history entry: a number is
 * read once, with the numbers of that moment. When the numbers change (a
 * drag, a "+", a hidden tile), the entry keeps the background it named, and
 * useKeepUrlBackgroundNumber writes its new number.
 */
const backgroundsOfEntries = new Map<string, string | null>();

/**
 * The navigation state key of a URL that the page rewrote: the search it
 * replaced (useKeepUrlBackgroundNumber).
 */
export const URL_REWRITE_OF = "urlRewriteOf";

export interface UrlBackgroundState {
  /** The identity of the background that the URL names, or null. */
  background: string | null;
  /** The URL names a number, and the numbers are not known yet. */
  isPending: boolean;
}

/** The background that the URL names: `?bg=<number>` or `?background=<identity>`. */
export function useUrlBackground(): UrlBackgroundState {
  const location = useLocation();
  const numbered = useBackgroundNumbers();
  const named = useMemo(() => readUrlBackground(new URLSearchParams(location.search)), [location.search]);
  if (!named) return {background: null, isPending: false};
  if (named.kind === "identity") return {background: named.background, isPending: false};
  const entry = `${location.key}${location.search}`;
  if (backgroundsOfEntries.has(entry)) {
    return {background: backgroundsOfEntries.get(entry)!, isPending: false};
  }
  if (!numbered) return {background: null, isPending: true};
  const background = backgroundOfNumber(named.number, numbered);
  backgroundsOfEntries.set(entry, background);
  return {background, isPending: false};
}

/**
 * Write a background into the URL params, by its number when it has one
 * (libs/bgNumbering.ts). For setSearchParams callbacks.
 */
export function useWriteUrlBackground(): (
  params: URLSearchParams,
  background: string | null
) => URLSearchParams {
  const numbered = useBackgroundNumbers();
  return useCallback(
    (params: URLSearchParams, background: string | null) =>
      writeUrlBackground(params, background, numbered),
    [numbered]
  );
}

/**
 * App: the URL shows the number of the background it names. An identity
 * (an old link, a tool) gives way to the number, and a new order of the
 * tiles gives the background its new number. A number that names no tile
 * leaves the URL.
 */
export function useKeepUrlBackgroundNumber(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const numbered = useBackgroundNumbers();
  const {background, isPending} = useUrlBackground();
  // A switch resets the page first (App.js); its arrival state must not be
  // written again.
  const isArriving = Boolean((location.state as any)?.resetOnArrival);
  useEffect(() => {
    if (!numbered || isPending || isArriving || !namesUrlBackground(searchParams)) return;
    const next = writeUrlBackground(new URLSearchParams(searchParams), background, numbered);
    if (next.toString() === searchParams.toString()) return;
    // The same background in other words, not a move of the user: the last
    // location follows it only where it held the URL before (AppRoot).
    const state = {...((location.state as object | null) ?? {}), [URL_REWRITE_OF]: location.search};
    setSearchParams(next, {replace: true, state});
  }, [numbered, background, isPending, isArriving, searchParams, setSearchParams, location.state]);
}
